import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GlossaryEmblem } from "@/components/GlossaryEmblem";
import { GLOSSARY, GLOSSARY_CATEGORIES, type GlossaryCategory } from "@/data/glossary";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/glossary/")({
  head: () => ({
    meta: [
      { title: `Mr. B Quotes Glossary — Quotation & Rhetoric Terms` },
      { name: "description", content: `Wondering what a pull quote, maxim, or chiasmus is? The Mr. B Quotes Glossary breaks down quotation terms with plain-English definitions and easy examples.` },
      { property: "og:title", content: "Mr. B Quotes Glossary" },
      { property: "og:description", content: `${GLOSSARY.length} in-depth terms on the language and culture of quotation.` },
      { property: "og:url", content: `${SITE}/glossary` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/glossary` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Mr. B Quotes Glossary",
          url: `${SITE}/glossary`,
          description: `A simple glossary that explains terms about quotation, attribution, and rhetoric.`,
          hasDefinedTerm: GLOSSARY.map((e) => ({
            "@type": "DefinedTerm",
            name: e.term,
            description: e.short,
            url: `${SITE}/glossary/${e.slug}`,
            inDefinedTermSet: `${SITE}/glossary`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Glossary", item: `${SITE}/glossary` },
          ],
        }),
      },
    ],
  }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<GlossaryCategory | "All">("All");

  const filtered = GLOSSARY.filter((e) => {
    if (cat !== "All" && e.category !== cat) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      e.term.toLowerCase().includes(needle) ||
      e.short.toLowerCase().includes(needle) ||
      e.body.some((p) => p.toLowerCase().includes(needle))
    );
  }).sort((a, b) => a.term.localeCompare(b.term));

  // A–Z map
  const byLetter = new Map<string, typeof GLOSSARY>();
  for (const e of filtered) {
    const k = e.term.charAt(0).toUpperCase();
    if (!byLetter.has(k)) byLetter.set(k, []);
    byLetter.get(k)!.push(e);
  }
  const letters = Array.from(byLetter.keys()).sort();

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Glossary" active="glossary" />

      {/* Masthead */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gradient-gold opacity-10 blur-3xl" aria-hidden />
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-12 sm:pt-20 sm:pb-14 relative">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <span className="text-foreground">Glossary</span>
          </nav>
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Reference</div>
          <h1 className="mt-2 font-display text-5xl sm:text-7xl leading-[0.95]">
            The Mr. B Quotes <span className="text-gold italic">Glossary</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            An in-depth, plain-English reference to the language and culture of quotation —
            <strong className="text-foreground"> {GLOSSARY.length} entries</strong> on aphorism and adage, attribution and misattribution, chiasmus and chiaroscuro of rhetoric.
            Built to be read, searched, and shared.
          </p>

          {/* Search + filter */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <label className="sr-only" htmlFor="glossary-search">Search the glossary</label>
            <input
              id="glossary-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 54+ terms — aphorism, sic, chiasmus…"
              className="flex-1 rounded-full border border-border bg-card/60 px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-gold transition"
            />
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value as GlossaryCategory | "All")}
              aria-label="Filter by category"
              className="rounded-full border border-border bg-card/60 px-5 py-3 text-sm text-foreground outline-none focus:border-gold transition"
            >
              <option value="All">All categories</option>
              {GLOSSARY_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* A–Z bar */}
          {letters.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-1.5 text-xs">
              {letters.map((l) => (
                <a key={l} href={`#letter-${l}`} className="rounded-md border border-border bg-card/40 px-2.5 py-1 font-mono text-gold hover:border-gold transition">
                  {l}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Entries by letter */}
      <main className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        {letters.length === 0 && (
          <p className="text-center text-muted-foreground">No entries match — try a different search.</p>
        )}
        {letters.map((l) => (
          <section key={l} id={`letter-${l}`} className="mb-12 scroll-mt-20">
            <div className="mb-5 flex items-baseline gap-4 border-b border-border/60 pb-2">
              <div className="font-display text-4xl text-gold">{l}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{byLetter.get(l)!.length} {byLetter.get(l)!.length === 1 ? "term" : "terms"}</div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {byLetter.get(l)!.map((e) => (
                <Link
                  key={e.slug}
                  to="/glossary/$slug"
                  params={{ slug: e.slug }}
                  className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-gold hover:shadow-elegant"
                >
                  <div className="aspect-[5/3] overflow-hidden">
                    <GlossaryEmblem slug={e.slug} term={e.term} category={e.category} variant="card" className="h-full w-full transition group-hover:scale-[1.02]" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{e.category}</div>
                    <h3 className="mt-1 font-display text-2xl text-foreground group-hover:text-gold transition">{e.term}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{e.short}</p>
                    <div className="mt-4 text-xs font-medium text-gold opacity-0 group-hover:opacity-100 transition">Read entry →</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>

      <SiteFooter />
    </div>
  );
}
